import _ from 'lodash';

import AutoAcceptIncomingCall from 'actions/phone_call/auto_accept_incoming_call';
import AgentVoiceCapabilitiesChecker from '../configuration/lib/agent_voice_capabilities_checker';
import { checkAndResetNavigatingToNext } from 'actions/conversation/lib/conversation_workflow';
import ErrorReporter from 'scripts/infrastructure/error_reporter';
import hasLiveConnection from 'actions/phone_call/lib/has_live_connection';
import qconsole from 'scripts/lib/qconsole';
import updateConnectionState from 'actions/health/lib/update_connection_state';
import ShowIncomingCallSystemNotification from 'actions/notification/show_incoming_call_system_notification';
import ShowPhoneCallObserverNotification from 'actions/notification/show_phone_call_observer_notification';
import { TwilioDeviceStatus } from 'models/connection_states';
import { PhoneCallObserverNotificationRecipientType } from 'models/notification/phone_call_observer_notification';

export default class PhoneGatewayObserver {
  constructor(context) {
    this.context = context;
    this.updateConnectionState = updateConnectionState.bind(this, 'twilio');
    this.agentVoiceCapabilitiesChecker = new AgentVoiceCapabilitiesChecker(this.context);
  }

  handleRegisteredAgent() {}

  handleRequestError(errorDto) {
    qconsole.log(`Phone gateway error occurred: ${JSON.stringify(errorDto)}`);
    this.handlePhoneStatus(TwilioDeviceStatus.OFFLINE);
  }

  handleAccept() {}

  handleAcceptError(message) {
    ErrorReporter.reportMessage(message, {
      extra: {
        connectionState: this.context.stores.connectionState.get(),
      },
    });
  }

  handleCall() {}

  handleCancelError(message) {
    qconsole.tail({ since: 30 });
    qconsole.error(message);
  }

  handleConnect() {
    this.updateConnectionState(state => state.setConnected());
  }

  handleDeviceError() {
    // Re-evaluate our device status permissions.
    // This will ensure we still have microphone permissions.
    this.context.gateways.deviceStatus.getStatus();
  }

  handleDisconnect() {
    this.updateConnectionState(state => state.setDisconnected());
  }

  handleTwilioEnabled() {
    let connectionStates = this.context.stores.connectionState.get();
    connectionStates.setTwilioEnabled();
    this.context.stores.connectionState.set(connectionStates);
  }

  handleTwilioDisabled() {
    let connectionStates = this.context.stores.connectionState.get();
    connectionStates.setTwilioDisabled();
    this.context.stores.connectionState.set(connectionStates);
  }

  handleActiveCall() {
    let currentAgent = this.context.stores.currentAgent.get();
    const connection = this.context.gateways.phoneGatewayHttp.connection;
    let callId = _.get(connection, 'parameters.CallSid', '');
    let appFeatures = this.context.stores.appFeatures.get();

    if (!this.agentVoiceCapabilitiesChecker.canHandlePhoneCalls()) {
      return this.handleMissingVoiceCapabilities(currentAgent, callId, appFeatures);
    }

    // Need to check for both pending and committed active calls just in case the mqtt success event comes in after the event from Twilio

    // The MQTT publish success event will typically be handled first where
    // we subscribe and commit the pending active call
    let activeCall = this.context.stores.activeCall.get();
    if (activeCall) {
      let phoneCallContent = activeCall.conversationItem.content;
      if (phoneCallContent.isOutgoing()) {
        this.context.gateways.phoneGatewayHttp.accept();
        return;
      }

      // If the agent is a observer participant of the call && is currently in dialing state, accept the
      // call leg so the agent can join the conference
      if (phoneCallContent.isParticipantObserver(currentAgent.id) && phoneCallContent.isDialing(currentAgent.id)) {
        this.context.gateways.phoneGatewayHttp.accept();
        this.context.executeAction(ShowPhoneCallObserverNotification, {
          message: 'You are now listening in on the call.',
          recipient: PhoneCallObserverNotificationRecipientType.OBSERVER,
        });
        return;
      }

      // Show green incoming call banner and system notification if there is an offered call by the time the phone connection is established
      // Delayed incoming call connection
      let hasLivePhoneConnection = hasLiveConnection(this.context, { activeCall });
      activeCall.setHasLiveConnection(hasLivePhoneConnection);
      this.context.stores.activeCall.set(activeCall);

      const navigatingToNext = checkAndResetNavigatingToNext(this.context);
      if (!navigatingToNext) {
        this.context.executeAction(ShowIncomingCallSystemNotification);
      }

      this.context.executeAction(AutoAcceptIncomingCall, {
        activeCall,
        currentAgentId: currentAgent.id,
      });

      return;
    }

    // it is possible that the activeCall is not committed in the store if the Twilio incoming call event is handled before the MQTT publish success event
    // in this case, we need to commit the pending active call to display the active call controls
    let pendingActiveCall = this.context.stores.activeCall.getPending();
    if (pendingActiveCall) {
      let phoneCallContent = pendingActiveCall.conversationItem.content;
      if (phoneCallContent.isOutgoing()) {
        this.context.stores.activeCall.commitPending();
        this.context.gateways.phoneGatewayHttp.accept();
      }
    }
  }

  handleMissingVoiceCapabilities(currentAgent, callId, appFeatures) {
    ErrorReporter.reportError(new Error('handleActiveCall: agent missing voice capabilities'), {
      extra: {
        agentId: currentAgent && currentAgent.id,
        callId,
        connectionState: this.context.stores.connectionState.get(),
        phonePreference: currentAgent && currentAgent.getVoiceConfigurationPreference(),
        microphonePermissions: appFeatures.isEnabled('microphone'),
      },
    });

    return;
  }

  handleHangup(conversationItemId, payload) {}

  handlePhoneControlsSuccess(conversationItemId, payload) {
    this.context.stores.activeCall.commitPending();
  }

  handlePhoneControlsErrors(conversationItemId, errorDto) {
    // Temporary work around for handling disconnect errors to ensure agents can always disconnect
    // from the call. Additional phone control error handling should be added in the future
    let pendingActiveCall = this.context.stores.activeCall.getPending();
    let currentAgentId = this.context.stores.currentAgent.get().id;
    let isCurrentAgentDisconnecting =
      pendingActiveCall && pendingActiveCall.conversationItem.content.isParticipantDisconnecting(currentAgentId);
    if (isCurrentAgentDisconnecting) {
      this.context.stores.activeCall.resetPending();
      return;
    }
    this.context.stores.activeCall.commitPending();

    // If the agent is a observer participant of the call, show error banner
    if (pendingActiveCall.isParticipantObserver(currentAgentId)) {
      this.context.executeAction(ShowPhoneCallObserverNotification, {
        message: 'Unable to listen in on call.',
        recipient: PhoneCallObserverNotificationRecipientType.OBSERVER,
      });
      return;
    }
  }

  handleCallQualityWarning(warningName) {
    this.handleCallQualityEvent(warningName, 'STARTED');
  }

  handleCallQualityWarningCleared(warningName) {
    this.handleCallQualityEvent(warningName, 'ENDED');
  }

  handleCallQualityEvent(warningName, status) {
    const activeCall = this.context.stores.activeCall.get();
    if (activeCall) {
      let currentAgentId = this.context.stores.currentAgent.get().id;
      let event = {
        eventType: warningName,
        status,
        agentId: currentAgentId,
      };

      this.context.gateways.phoneControlsHttp.recordQualityEvent(
        activeCall.customer.id,
        activeCall.conversationItem.id,
        event
      );
    }
  }

  /*
  Updates the Twilio connection status to be either ready, busy, or offline.
  - Status will be updated to 'ready' when device becomes available to take calls.
  - Status will be updated to 'busy' when device has an active call or is setup but not available.
  - Status will be updated to 'offline' when device is not setup or after a valid token expires
  */
  handlePhoneStatus(status) {
    if (_.keys(TwilioDeviceStatus).indexOf(status) === -1) {
      ErrorReporter.reportMessage(`could not handle browser phone status`, {
        extra: {
          status,
        },
      });
      return;
    }

    let connectionStates = this.context.stores.connectionState.get();
    connectionStates.updateTwilioStatus(status);
    this.context.stores.connectionState.set(connectionStates);
  }
}
